import React, { useState, useEffect, useRef } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { app } from './firebase';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import EnvironmentManager from '../config/EnvironmentManager';
import './VideoCoursesView.css';
import useVideoProgress from './useVideoProgress';

import { 
  Search, BookOpen, Clock, Users, Award, 
  ChevronDown, ChevronRight, Star, Play, Bookmark,
  Grid, List, RefreshCw, Upload, User,
  ArrowLeft, CheckCircle, Video,
  FileText, Download, Globe, Calendar, Edit, Trash2, 
  Archive, Settings, FolderOpen, Eye, BarChart,
  Home, Bell, Grid3x3, PieChart, LogOut, ChevronLeft,
  Mail, HelpCircle, Shield, CreditCard, ExternalLink,
  X
} from "lucide-react";

// Course Actions Dropdown Component
const CourseActionsDropdown = ({ course, onEdit, onDelete, onArchive, onToggle, isArchived = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation(); 
    setIsOpen(!isOpen);
    if (onToggle) onToggle(e);
  };

  const handleEdit = (e) => {
    e.stopPropagation(); 
    setIsOpen(false);
    onEdit(course);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onDelete(course);
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    setIsOpen(false);
    onArchive(course);
  };

  return (
    <div className="icv-course-actions-dropdown" ref={dropdownRef}>
      <button 
        className="icv-dropdown-toggle"
        onClick={handleToggle}
      >
        <span>•••</span>
      </button>
      
      {isOpen && (
        <div className="icv-dropdown-menu">
          {!isArchived ? (
            <>
              <button className="icv-dropdown-item" onClick={handleEdit}>
                <Edit size={14} /> Edit Course
              </button>
              <button className="icv-dropdown-item" onClick={handleArchive}>
                <Archive size={14} /> Archive Course
              </button>
              <div className="icv-dropdown-divider"></div>
              <button className="icv-dropdown-item icv-dropdown-item-danger" onClick={handleDelete}>
                <Trash2 size={14} /> Delete Course
              </button>
            </>
          ) : (
            <>
              <button className="icv-dropdown-item" onClick={handleArchive}>
                <Settings size={14} /> Unarchive Course
              </button>
              <div className="icv-dropdown-divider"></div>
              <button className="icv-dropdown-item icv-dropdown-item-danger" onClick={handleDelete}>
                <Trash2 size={14} /> Delete Permanently
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Course Stats Component
const CourseStats = ({ course }) => {
  const totalVideos = getTotalVideosCount(course);
  const totalDuration = getTotalDuration(course);
  
  return (
    <div className="icv-course-stats">
      <div className="icv-stat-item">
        <Video size={12} />
        <span>{totalVideos} videos</span>
      </div>
      <div className="icv-stat-item">
        <Clock size={12} />
        <span>{totalDuration}</span>
      </div>
      <div className="icv-stat-item">
        <Users size={12} />
        <span>{course.enrolledCount?.toLocaleString() || '0'} enrolled</span>
      </div>
    </div>
  );
};

// User Dropdown Component for Header
const UserDropdown = ({ user, onLogout, onNavigate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = () => {
    onLogout();
    setIsOpen(false);
  };

  const handleNavigate = (path) => {
    onNavigate(path);
    setIsOpen(false);
  };

  return (
    <div className="icv-user-dropdown" ref={dropdownRef}>
      <button 
        className="icv-user-dropdown-toggle"
        onClick={handleToggle}
      >
        <div className="icv-header-user-avatar">
          {user?.email?.[0]?.toUpperCase() || 'U'}
        </div>
        <ChevronDown size={16} />
      </button>
      
      {isOpen && (
        <div className="icv-user-dropdown-menu">
          <div className="icv-user-dropdown-header">
            <div className="icv-user-dropdown-avatar">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="icv-user-dropdown-info">
              <span className="icv-user-dropdown-name">
                {user?.displayName || user?.email?.split('@')[0] || 'User'}
              </span>
              <span className="icv-user-dropdown-email">{user?.email || 'user@example.com'}</span>
            </div>
          </div>
          
          <div className="icv-user-dropdown-divider"></div>
          
          <button className="icv-user-dropdown-item" onClick={() => handleNavigate('profile')}>
            <User size={16} />
            <span>My Profile</span>
          </button>
          
          <button className="icv-user-dropdown-item" onClick={() => handleNavigate('settings')}>
            <Settings size={16} />
            <span>Account Settings</span>
          </button>
          
          <button className="icv-user-dropdown-item" onClick={() => handleNavigate('billing')}>
            <CreditCard size={16} />
            <span>Billing & Plans</span>
          </button>
          
          <div className="icv-user-dropdown-divider"></div>
          
          <button className="icv-user-dropdown-item" onClick={() => handleNavigate('help')}>
            <HelpCircle size={16} />
            <span>Help & Support</span>
          </button>
          
          <button className="icv-user-dropdown-item" onClick={() => handleNavigate('privacy')}>
            <Shield size={16} />
            <span>Privacy Policy</span>
          </button>
          
          <div className="icv-user-dropdown-divider"></div>
          
          <button className="icv-user-dropdown-item icv-user-dropdown-logout" onClick={handleLogout}>
            <LogOut size={16} />
            <span>Log Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Professional Course Card Component
const InstructorCourseCard = ({ course, onCourseSelect, onEditCourse, onDeleteCourse, onArchiveCourse, isArchived = false }) => {
  const handleCardClick = (e) => {
    if (e.target.closest('.icv-course-actions-dropdown') || 
        e.target.closest('.icv-dropdown-toggle') ||
        e.target.closest('.icv-dropdown-menu') ||
        e.target.closest('.icv-dropdown-item')) {
      return;
    }
    onCourseSelect(course);
  };

  const handleDropdownToggle = (e) => {
    e.stopPropagation(); 
  };

  return (
    <div 
      className={`icv-course-card ${isArchived ? 'archived' : ''}`}
      onClick={() => onCourseSelect(course)}
    >
      {/* Course Header */}
      <div className="icv-course-header">
        <div className="icv-course-status">
          {isArchived ? (
            <span className="icv-course-status-badge archived">
              <Archive size={10} />
              Archived
            </span>
          ) : (
            <span className="icv-course-status-badge published">
              <Eye size={10} />
              Published
            </span>
          )}
        </div>
        <div className="icv-course-actions">
          <CourseActionsDropdown 
            course={course}
            onEdit={onEditCourse}
            onDelete={onDeleteCourse}
            onArchive={onArchiveCourse}
            onToggle={handleDropdownToggle}
            isArchived={isArchived}
          />
        </div>
      </div>

      {/* Course Content */}
      <div className="icv-course-content">
        <div className="icv-course-category">{course.category || 'General'}</div>
        <h3 className="icv-course-title">{course.title || 'Untitled Course'}</h3>
        <p className="icv-course-description">
          {course.description || 'No description available'}
        </p>
        
        <CourseStats course={course} />
      </div>

      {/* Course Footer */}
      <div className="icv-course-footer">
        <div className="icv-course-instructor">
          <User size={12} />
          <span>{course.instructor || 'You'}</span>
        </div>
        <div className="icv-course-actions-footer">
          <button className="icv-course-action-btn preview" onClick={(e) => e.stopPropagation()}>
            <Eye size={12} />
            Preview
          </button>
          <button className="icv-course-action-btn stats" onClick={(e) => e.stopPropagation()}>
            <BarChart size={12} />
            Stats
          </button>
        </div>
      </div>
    </div>
  );
};

// Course Detail View Component - FIXED WITH VIDEO PLAYER
const CourseDetailView = ({ course, onBack, user }) => {
  const [expandedModules, setExpandedModules] = useState({});
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [currentVideo, setCurrentVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [completeCourseData, setCompleteCourseData] = useState(null);
  const [loadingCompleteData, setLoadingCompleteData] = useState(true);
  const [courseStats, setCourseStats] = useState({
    totalVideos: 0,
    totalDuration: 0,
    totalModules: 0,
    totalSubmodules: 0
  });
  const videoRef = useRef(null);
  const navigate = useNavigate();

  const { 
    saveLessonProgress, 
    getLessonProgress, 
    getAllProgress 
  } = useVideoProgress(user?.uid, course?.courseId);

  const getIdToken = async () => {
    const user = getAuth(app).currentUser;
    return user ? await user.getIdToken(true) : null;
  };

  // Fetch complete course data when component mounts
  useEffect(() => {
    const fetchCompleteCourseData = async () => {
      if (!course?.courseId || !user?.uid) return;
      
      setLoadingCompleteData(true);
      try {
        const idToken = await getIdToken();
        const response = await axios.get(
          `${EnvironmentManager.baseUrl}/mv-profile/video-courses/course-with-excel/${course.courseId}`,
          { headers: { 'Authorization': `Bearer ${idToken}` } }
        );
        
        console.log('Complete course data fetched:', response.data);
        
      
        const courseData = processCourseData(response.data);
        setCompleteCourseData(courseData);
        
     
        calculateCourseStats(courseData);
        
      } catch (error) {
        console.error('Error fetching complete course:', error);
        
        setCompleteCourseData(course);
        calculateCourseStats(course);
      } finally {
        setLoadingCompleteData(false);
      }
    };

    fetchCompleteCourseData();
  }, [course?.courseId, user]);


  const processCourseData = (data) => {
    let courseInfo = {};
    let modules = [];
    
    
    if (data.courseId || data.title) {
      courseInfo = {
        courseId: data.courseId || course.courseId,
        title: data.title || 'Untitled Course',
        description: data.description || '',
        instructor: data.instructor || '',
        category: data.category || '',
        language: data.language || 'English',
        level: data.level || 'Beginner',
        price: data.price || '',
        userType: data.userType || 'All',
        duration: data.duration || '',
        type: data.type || '',
        createdAt: data.createdDate || '',
        lastUpdated: data.updatedDate || '',

        rating: data.rating || 4.5,
        students: data.students || 0,
        enrolledCount: data.enrolledCount || 0,
       
        lectures: data.lectures || 0,
        sections: data.sections || 0
      };
      
     
      if (Array.isArray(data.modules)) {
        modules = data.modules;
      } else if (data.videoModules) {
        modules = data.videoModules;
      } else if (data.modulesData) {
        modules = data.modulesData;
      }
    } else if (data.courseInfo && data.modules) {
      courseInfo = data.courseInfo;
      modules = data.modules;
    }
    

    const processedModules = modules.map((module, moduleIndex) => {
      const submodules = module.submodules || module.videoSubModules || module.subModules || [];
      
      const processedSubmodules = submodules.map((submodule, subIndex) => {
        
        const videos = submodule.coursevideo || 
                       submodule.videoCourseMedia || 
                       submodule.videos || 
                       submodule.courseVideo || 
                       [];
        
       
        const processedVideos = videos.map((video, videoIndex) => ({
          videoId: video.courseVideoId || video.videoId || `video-${moduleIndex}-${subIndex}-${videoIndex}`,
          title: video.videoCourseTitle || video.title || video.videoTitle || `Video ${videoIndex + 1}`,
          description: video.videoCourseDescription || video.description || '',
          duration: video.duration || '0:00',
          videoUrl: video.videoCourseUrl || video.videoUrl || video.url || video.videoCourseUrl,
          thumbnail: video.thumbnail || video.videoThumbnail,
          isPreview: video.isPreview || false
        }));
        
        return {
          subModuleId: submodule.subModuleId || submodule.id || `sub-${moduleIndex}-${subIndex}`,
          title: submodule.title || `Submodule ${subIndex + 1}`,
          description: submodule.description || '',
          order: submodule.order || subIndex,
          videos: processedVideos,
          coursevideo: processedVideos
        };
      });
      
      return {
        moduleId: module.moduleId || module.id || `module-${moduleIndex}`,
        title: module.title || `Module ${moduleIndex + 1}`,
        description: module.description || '',
        order: module.order || moduleIndex,
        submodules: processedSubmodules,
        videoSubModules: processedSubmodules 
      };
    });
    
    return {
      ...courseInfo,
      modules: processedModules,
      videoModules: processedModules 
    };
  };

  // Calculate course statistics
  const calculateCourseStats = (courseData) => {
    if (!courseData?.modules) return;
    
    let totalVideos = 0;
    let totalModules = courseData.modules.length;
    let totalSubmodules = 0;
    
    courseData.modules.forEach(module => {
      const submodules = module.submodules || module.videoSubModules || [];
      totalSubmodules += submodules.length;
      
      submodules.forEach(submodule => {
        const videos = submodule.videos || submodule.coursevideo || [];
        totalVideos += videos.length;
      });
    });
    
    // Calculate duration (assuming 30 minutes per video as default)
    const totalMinutes = totalVideos * 30;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const totalDuration = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    
    setCourseStats({
      totalVideos,
      totalDuration,
      totalModules,
      totalSubmodules
    });
  };

  const displayCourse = completeCourseData || course;

  const toggleModule = (moduleIndex) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleIndex]: !prev[moduleIndex]
    }));
  };

  const toggleLessonCompletion = (lessonId) => {
    setCompletedLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
      }
      return newSet;
    });
  };

  // FIXED: Edit Course function - goes to intermediate page
  const handleEditCourse = () => {
    navigate('/MHMV-profile/edit-course-options', {
      state: {
        courseId: course.courseId,
        courseData: displayCourse,
        fromCourseDetail: true
      }
    });
  };

  // FIXED: Edit Video function - goes to intermediate page
  const handleEditVideo = (videoData = null) => {
    navigate('/MHMV-profile/edit-course-options', {
      state: {
        courseId: course.courseId,
        courseData: displayCourse,
        fromCourseDetail: true,
        videoToEdit: videoData,
        defaultOption: 'videos' // Pre-select videos option
      }
    });
  };

  // FIXED: Edit Submodule function - goes to intermediate page
  const handleEditSubmodule = (moduleIndex, submoduleIndex) => {
    navigate('/MHMV-profile/edit-course-options', {
      state: {
        courseId: course.courseId,
        courseData: displayCourse,
        fromCourseDetail: true,
        editSection: {
          moduleIndex,
          submoduleIndex
        },
        defaultOption: 'both' // Pre-select both option
      }
    });
  };

  // FIXED VIDEO PLAYBACK FUNCTION
  const playVideo = async (videoUrl, lessonId, lessonTitle) => {
    console.log('=== PLAY VIDEO FUNCTION STARTED ===');
    console.log('Input videoUrl:', videoUrl);
    console.log('Lesson ID:', lessonId);
    console.log('Lesson Title:', lessonTitle);
    
    if (!videoUrl) {
      console.error('No video URL provided');
      alert('Video URL not available. Please check the video configuration.');
      return;
    }
    
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        alert('Please login to view videos');
        return;
      }

      
      let cleanVideoUrl = videoUrl;
      
    
      cleanVideoUrl = cleanVideoUrl.replace(/^[:]+/, '').trim();
      
      
      if (cleanVideoUrl.startsWith('http')) {
        console.log('Using direct HTTP URL');
        setCurrentVideo({ 
          url: cleanVideoUrl, 
          lessonId,
          lessonTitle,
          startTime: 0
        });
        return;
      }
      
      
      console.log('Cleaned video path:', cleanVideoUrl);
      

      const downloadUrl = `${EnvironmentManager.baseUrl}/mv-profile/files/download?fileUrl=${encodeURIComponent(cleanVideoUrl)}`;
      
      console.log('Constructed download URL:', downloadUrl);
      
      const savedProgress = getLessonProgress(lessonId);
      const startTime = savedProgress?.currentTime || 0;
      
      console.log('Fetching video from server...');
      
     
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${idToken}`,
          'Accept': 'video/*'
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}: ${response.statusText}`);
      }
      
      
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      const videoBlob = await response.blob();
      console.log('Blob received - Size:', videoBlob.size, 'Type:', videoBlob.type);
      
      if (videoBlob.size === 0) {
        throw new Error('Received empty video file');
      }
      
    
      const videoBlobUrl = URL.createObjectURL(videoBlob);
      console.log('Blob URL created successfully');
      
      
      if (currentVideo?.url && currentVideo.url.startsWith('blob:')) {
        URL.revokeObjectURL(currentVideo.url);
      }
      
      
      setCurrentVideo({ 
        url: videoBlobUrl, 
        lessonId,
        lessonTitle,
        startTime
      });
      
      console.log('Video state set, modal should appear');
      
    } catch (error) {
      console.error('Error in playVideo:', error);
      
  
      console.log('Trying alternative approach...');
      
     
      if (videoUrl.includes('video_course/videos/')) {
        const directUrl = `${EnvironmentManager.baseUrl}/${videoUrl.replace(/^:/, '')}`;
        console.log('Trying direct URL:', directUrl);
        
        setCurrentVideo({ 
          url: directUrl, 
          lessonId,
          lessonTitle,
          startTime: 0
        });
      } else {
        alert(`Failed to load video: ${error.message}\n\nPlease check:\n1. Video file exists on server\n2. You have proper permissions\n3. Video path format is correct`);
      }
    }
  };

  useEffect(() => {
    if (currentVideo && videoRef.current) {
      videoRef.current.currentTime = currentVideo.startTime;
    }
  }, [currentVideo]);

  if (loadingCompleteData) {
    return (
      <div className="icv-course-detail">
        <div className="icv-detail-header">
          <button onClick={onBack} className="icv-back-btn">
            <ArrowLeft size={18} />
            Back to Courses
          </button>
        </div>
        <div className="icv-loading-state">
          <div className="icv-loading-spinner"></div>
          <p>Loading complete course details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="icv-course-detail">
      {/* Header */}
      <div className="icv-detail-header">
        <button onClick={onBack} className="icv-back-btn">
          <ArrowLeft size={18} />
          Back to Courses
        </button>
        <div className="icv-course-breadcrumb">
          <span>My Courses</span>
          <ChevronRight size={14} />
          <span>{displayCourse.category || 'General'}</span>
          <ChevronRight size={14} />
          <span>{displayCourse.title || 'Untitled Course'}</span>
        </div>
      </div>

      <div className="icv-detail-content">
        {/* Main Content */}
        <div className="icv-detail-main">
          {/* Course Overview */}
          <div className="icv-course-overview">
            <div className="icv-overview-header">
              <div>
                <span className="icv-course-category-badge">{displayCourse.category || 'General'}</span>
                <h1 className="icv-course-title-main">{displayCourse.title || 'Untitled Course'}</h1>
                <p className="icv-course-description-main">{displayCourse.description || 'No description available'}</p>
              </div>
              <div className="icv-course-extra-info">
                  <span><b>Language:</b> {displayCourse.language || 'N/A'}</span>
                  <span><b>Level:</b> {displayCourse.level || 'N/A'}</span>
                  <span><b>Price:</b> {displayCourse.price ? `${displayCourse.price}` : 'Free'}</span>
                </div>

              <div className="icv-course-actions-header">
                <button 
                  className="icv-action-btn primary"
                  onClick={handleEditCourse}
                >
                  <Edit size={16} />
                  Edit Course
                </button>
                
              </div>
            </div>

            <div className="icv-course-metrics">
              <div className="icv-metric">
                <div className="icv-metric-label">Total Students</div>
                <div className="icv-metric-value">{displayCourse.enrolledCount?.toLocaleString() || displayCourse.students?.toLocaleString() || '0'}</div>
              </div>
              <div className="icv-metric">
                <div className="icv-metric-label">Total Videos</div>
                <div className="icv-metric-value">{courseStats.totalVideos}</div>
              </div>
              <div className="icv-metric">
                <div className="icv-metric-label">Course Duration</div>
                <div className="icv-metric-value">{courseStats.totalDuration}</div>
              </div>
              <div className="icv-metric">
                <div className="icv-metric-label">Status</div>
                <div className="icv-metric-value status-published">Published</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="icv-detail-tabs">
            <button 
              className={`icv-tab ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              Course Content
            </button>
            <button 
              className={`icv-tab ${activeTab === 'analytics' ? 'active' : ''}`}
              onClick={() => setActiveTab('analytics')}
            >
              Analytics
            </button>
            
          </div>

          {/* Course Content */}
          {activeTab === 'content' && (
            <div className="icv-course-curriculum">
              <div className="icv-curriculum-header">
                <h3>Course Curriculum</h3>
                <div className="icv-curriculum-stats">
                  <span>{courseStats.totalModules} modules</span>
                  <span>•</span>
                  <span>{courseStats.totalSubmodules} lessons</span>
                  <span>•</span>
                  <span>{courseStats.totalVideos} videos</span>
                  <span>•</span>
                  <span>{courseStats.totalDuration} total</span>
                </div>
                <button 
                  className="icv-add-module-btn"
                  onClick={handleEditCourse}
                >
                  + Add Module
                </button>
              </div>
              
              {displayCourse.modules?.length > 0 ? (
                displayCourse.modules.map((module, moduleIndex) => {
                  const submodules = module.submodules || module.videoSubModules || [];
                  
                  return (
                    <div key={module.moduleId || moduleIndex} className="icv-curriculum-module">
                      <div 
                        className="icv-module-header"
                        onClick={() => toggleModule(moduleIndex)}
                      >
                        <div className="icv-module-info">
                          <div className="icv-module-number">Module {moduleIndex + 1}</div>
                          <h4>{module.title || `Module ${moduleIndex + 1}`}</h4>
                          <div className="icv-module-meta">
                            <span>{submodules.length} lessons</span>
                            <span>•</span>
                            <span>{submodules.reduce((total, sub) => total + (sub.videos?.length || sub.coursevideo?.length || 0), 0)} videos</span>
                          </div>
                        </div>
                        <div className="icv-module-toggle">
                          {expandedModules[moduleIndex] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </div>
                      </div>

                      {expandedModules[moduleIndex] && (
                        <div className="icv-lessons-list">
                          {submodules.map((submodule, submoduleIndex) => {
                            const videos = submodule.videos || submodule.coursevideo || [];
                            
                            return (
                              <div 
                                key={submodule.subModuleId || submoduleIndex}
                                className="icv-submodule-section"
                              >
                                <div className="icv-submodule-header">
                                  <div className="icv-submodule-info">
                                    <div className="icv-submodule-number">{moduleIndex + 1}.{submoduleIndex + 1}</div>
                                    <div>
                                      <h5>{submodule.title || `Lesson ${submoduleIndex + 1}`}</h5>
                                      {submodule.description && (
                                        <p className="icv-submodule-description">{submodule.description}</p>
                                      )}
                                      <div className="icv-submodule-meta">
                                        <span>{videos.length} videos</span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                </div>
                                
                                {videos.length > 0 && (
                                  <div className="icv-videos-list">
                                    {videos.map((video, videoIndex) => {
                                      const lessonId = `${moduleIndex}-${submoduleIndex}-${videoIndex}`;
                                      const isCompleted = completedLessons.has(lessonId);
                                      const hasVideoUrl = video.videoUrl || video.url || video.videoCourseUrl;
                                      
                                      return (
                                        <div 
                                          key={video.videoId || videoIndex}
                                          className="icv-lesson-item"
                                        >
                                          <div className="icv-lesson-info">
                                            <div className="icv-lesson-number">{videoIndex + 1}</div>
                                            <div className="icv-lesson-content">
                                              <h6>{video.title || `Video ${videoIndex + 1}`}</h6>
                                              <div className="icv-lesson-meta">
                                                <Video size={12} />
                                                <span>Video • {video.duration || '0:00'}</span>
                                                {video.isPreview && (
                                                  <span className="icv-preview-badge">Preview</span>
                                                )}
                                                <span className={`icv-video-status-badge ${hasVideoUrl ? 'available' : 'missing'}`}>
                                                  {hasVideoUrl ? 'Video Available' : 'No Video'}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="icv-lesson-actions">
                                            {/* Play Button - Always shown */}
                                            <button 
                                              className={`icv-play-lesson-btn ${!hasVideoUrl ? 'disabled' : ''}`}
                                              onClick={() => hasVideoUrl && playVideo(hasVideoUrl, lessonId, video.title)}
                                              disabled={!hasVideoUrl}
                                              title={hasVideoUrl ? "Play this video" : "No video file available"}
                                            >
                                              <Play size={12} />
                                              {hasVideoUrl ? 'Play' : 'No Video'}
                                            </button>

                                            {/* Conditional Add/Edit Button */}
                                            {hasVideoUrl ? (
                                              <button 
                                                className="icv-edit-lesson-btn"
                                                onClick={() => handleEditVideo(video)}
                                                title="Edit this video"
                                              >
                                                <Edit size={12} />
                                                Edit Video
                                              </button>
                                            ) : (
                                              <button 
                                                className="icv-add-video-btn"
                                                onClick={() => handleEditVideo(video)}
                                                title="Add video to this lesson"
                                              >
                                                <Upload size={12} />
                                                Add Video
                                              </button>
                                            )}

                                            {/* Completion Button */}
                                            {isCompleted ? (
                                              <button 
                                                className="icv-complete-btn completed"
                                                onClick={() => toggleLessonCompletion(lessonId)}
                                              >
                                                <CheckCircle size={12} />
                                                Completed
                                              </button>
                                            ) : (
                                              <button 
                                               
                                              >
                                                
                                                
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="icv-empty-curriculum">
                  <Video size={40} />
                  <h4>No Course Content Yet</h4>
                  <p>This course doesn't have any modules or videos yet.</p>
                  <button 
                    className="icv-add-content-btn"
                    onClick={handleEditCourse}
                  >
                    <Upload size={14} />
                    Add Course Content
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="icv-analytics-tab">
              <h3>Course Analytics</h3>
              <div className="icv-analytics-placeholder">
                <BarChart size={48} />
                <p>Analytics data will appear here</p>
              </div>
            </div>
          )}

          
          
        </div>

        {/* Sidebar */}
        <div className="icv-detail-sidebar">
          <div className="icv-sidebar-card">
            <h4>Course Status</h4>
            <div className="icv-status-indicator published">
              <Eye size={16} />
              <span>Published</span>
            </div>
            
          </div>

          <div className="icv-sidebar-card">
            <h4>Course Statistics</h4>
            <div className="icv-quick-stats">
              <div className="icv-quick-stat">
                <span className="icv-quick-stat-label">Modules</span>
                <span className="icv-quick-stat-value">{courseStats.totalModules}</span>
              </div>
              <div className="icv-quick-stat">
                <span className="icv-quick-stat-label">Lessons</span>
                <span className="icv-quick-stat-value">{courseStats.totalSubmodules}</span>
              </div>
              <div className="icv-quick-stat">
                <span className="icv-quick-stat-label">Videos</span>
                <span className="icv-quick-stat-value">{courseStats.totalVideos}</span>
              </div>
              <div className="icv-quick-stat">
                <span className="icv-quick-stat-label">Students</span>
                <span className="icv-quick-stat-value">{displayCourse.enrolledCount?.toLocaleString() || displayCourse.students?.toLocaleString() || '0'}</span>
              </div>
            </div>
          </div>

          <div className="icv-sidebar-card">
            <h4>Course Information</h4>
            <div className="icv-course-info">
              <div className="icv-info-item">
                <span className="icv-info-label">Course ID</span>
                <span className="icv-info-value">{displayCourse.courseId || 'N/A'}</span>
              </div>
              <div className="icv-info-item">
                <span className="icv-info-label">Created</span>
                <span className="icv-info-value">
                {displayCourse.createdAt || 'N/A'}
              </span>

              </div>
              <div className="icv-info-item">
                <span className="icv-info-label">Last Updated</span>
                <span className="icv-info-value">
                {displayCourse.lastUpdated || 'N/A'}
              </span>

              </div>
              <div className="icv-info-item">
                <span className="icv-info-label">Instructor</span>
                <span className="icv-info-value">{displayCourse.instructor || 'You'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

     
      {currentVideo && (
        <div className="icv-video-modal-overlay" onClick={() => setCurrentVideo(null)}>
          <div className="icv-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="icv-video-modal-header">
              <div>
                <h3>{currentVideo.lessonTitle}</h3>
                <p className="icv-video-lesson-subtitle">Playing now</p>
              </div>
              <button 
                className="icv-video-close-btn"
                onClick={() => setCurrentVideo(null)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="icv-video-player-container">
              <video
                ref={videoRef}
                src={currentVideo.url}
                controls
                autoPlay
                onLoadedMetadata={() => {
                  console.log('Video metadata loaded');
                  if (videoRef.current && currentVideo.startTime > 0) {
                    console.log('Setting start time to:', currentVideo.startTime);
                    videoRef.current.currentTime = currentVideo.startTime;
                  }
                }}
                onTimeUpdate={() => {
                  if (videoRef.current) {
                    const progress = {
                      currentTime: videoRef.current.currentTime,
                      duration: videoRef.current.duration,
                      completed: videoRef.current.currentTime / videoRef.current.duration > 0.95
                    };
                    console.log('Saving progress:', progress);
                    saveLessonProgress(currentVideo.lessonId, progress);
                  }
                }}
                onError={(e) => {
                  console.error('Video error:', e.target.error);
                  alert(`Video playback error: ${e.target.error?.message || 'Unknown error'}`);
                }}
                className="icv-video-player"
              >
                Your browser does not support the video tag.
              </video>
            </div>
            
            <div className="icv-video-modal-footer">
              
              <button 
                className="icv-video-download-btn"
                onClick={() => window.open(currentVideo.url, '_blank')}
              >
                <Download size={16} />
                Download Video
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Component
const InstructorCoursesView = () => {
  const auth = getAuth(app);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [detailView, setDetailView] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [courseToArchive, setCourseToArchive] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [activeSidebarItem, setActiveSidebarItem] = useState('view-courses');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        await fetchVideoCourses(currentUser.uid);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchVideoCourses = async (userId) => {
    try {
      setLoading(true);
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.getVideoCourseDetailsByUser}/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setCourses(response.data || []);
      setFilteredCourses(response.data || []);

    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getIdToken = async () => {
    const user = getAuth(app).currentUser;
    return user ? await user.getIdToken(true) : null;
  };

  const handleNavigation = (page) => {
    switch (page) {
      case 'login':
        navigate('/MHMV-profile/jobseeker-login');
        break;
      case 'signup':
        navigate('/MHMV-profile/modal-register');
        break;
      case 'upload-excel':
        navigate('/MHMV-profile/upload-course-excel');
        break;
      case 'upload-video':
        navigate('/MHMV-profile/upload-video-course');
        break;
      case 'my-courses':
        navigate('/MHMV-profile/video-courses');
        break;
      case 'manage-courses':
        navigate('/MHMV-profile/manage-courses');
        break;
      case 'dashboard':
        navigate('/MHMV-profile/instructor-dashboard');
        break;
      case 'analytics':
        navigate('/MHMV-profile/instructor-analytics');
        break;
      case 'profile':
        navigate('/MHMV-profile/user-profile');
        break;
      case 'settings':
        navigate('/MHMV-profile/settings');
        break;
      case 'billing':
        navigate('/MHMV-profile/billing');
        break;
      case 'help':
        navigate('/MHMV-profile/help');
        break;
      case 'privacy':
        navigate('/MHMV-profile/privacy');
        break;
      case 'video-course-home':
        navigate('/MHMV-profile/video-course-home');
        break;
      default:
        navigate('/MHMV-profile/video-courses');
        break;
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/MHMV-profile/jobseeker-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = courses.filter(course =>
      course.title?.toLowerCase().includes(query.toLowerCase()) ||
      course.description?.toLowerCase().includes(query.toLowerCase()) ||
      course.category?.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCourses(filtered);
  };

  // FIXED: Edit course now navigates to Edit Options Page
  const handleEditCourse = (course) => {
    navigate('/MHMV-profile/edit-course-options', {
      state: { 
        courseId: course.courseId,
        courseData: course
      } 
    });
  };

  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete || !user) return;
    
    setActionLoading(true);
    try {
      const idToken = await getIdToken();
      
      if (!idToken) {
        setError('Authentication required');
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.deleteVideoCourseDetails}/${courseToDelete.courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.includes('deleted successfully')) {
        setCourses(prev => prev.filter(c => c.courseId !== courseToDelete.courseId));
        setFilteredCourses(prev => prev.filter(c => c.courseId !== courseToDelete.courseId));
        showMessage('Course deleted successfully');
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('Failed to delete course. Please try again.');
    } finally {
      setActionLoading(false);
      setShowDeleteConfirm(false);
      setCourseToDelete(null);
    }
  };
  
  const handleArchiveCourse = async (course) => {
    if (!course || !user) return;

    setActionLoading(true);

    try {
      const idToken = await getIdToken();
      if (!idToken) {
        showMessage('Authentication required');
        return;
      }

      const response = await axios.delete(
        `${EnvironmentManager.baseUrl}${EnvironmentManager.endpoints.archiveVideoCourse}/${course.courseId}`,
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.includes('archived successfully')) {
        setCourses(prev => prev.filter(c => c.courseId !== course.courseId));
        setFilteredCourses(prev => prev.filter(c => c.courseId !== course.courseId));
        showMessage('Course archived successfully');
      }

    } catch (error) {
      console.error('Archive error:', error);
      showMessage('Failed to archive course. Please try again.');
    } finally {
      setActionLoading(false);
      setShowArchiveConfirm(false);
      setCourseToArchive(null);
    }
  };

  const showMessage = (text) => {
    setError(text); 
    setTimeout(() => setError(''), 5000);
  };

  const handleCourseSelect = (course) => {
    setSelectedCourse(course);
    setDetailView(true);
  };

  const handleBackToList = () => {
    setDetailView(false);
    setSelectedCourse(null);
  };

  const handleRefresh = () => {
    if (user) {
      fetchVideoCourses(user.uid);
    }
  };

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  const handleSidebarItemClick = (item, path = null) => {
    setActiveSidebarItem(item);
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <div className="icv-container">
        <div className="icv-loading-state">
          <div className="icv-loading-spinner"></div>
          <p>Loading your courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="icv-container">
      {/* Collapsible Sidebar */}
      <aside 
        className={`icv-sidebar ${sidebarExpanded ? 'expanded' : ''}`}
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
      >
        <div className="icv-sidebar-inner">
          {/* Logo */}
          <div className="icv-sidebar-logo">
            <BookOpen size={24} className="icv-sidebar-logo-icon" />
            {sidebarExpanded && <span className="icv-sidebar-logo-text">MHMV</span>}
          </div>

          {/* Navigation Items */}
          <nav className="icv-sidebar-nav">
            

            <button 
              className={`icv-sidebar-item ${activeSidebarItem === 'manage-courses' ? '' : ''}`}
              onClick={() => {
                handleSidebarItemClick('manage-courses', '/MHMV-profile/manage-courses');
              }}
            >
              <FolderOpen size={20} />
              {sidebarExpanded && <span>Manage Courses</span>}
            </button>

            <button 
              className={`icv-sidebar-item ${activeSidebarItem === 'view-courses' ? 'active' : ''}`}
              onClick={() => handleSidebarItemClick('view-courses')}
            >
              <BookOpen size={20} />
              {sidebarExpanded && <span>My Courses</span>}
            </button>

            <button 
              className={`icv-sidebar-item ${activeSidebarItem === 'upload-courses' ? '' : ''}`}
              onClick={() => {
                handleSidebarItemClick('upload-courses', '/MHMV-profile/upload-course-excel');
              }}
            >
              <Upload size={20} />
              {sidebarExpanded && <span>Create Course</span>}
            </button>

            <button 
              className={`icv-sidebar-item ${activeSidebarItem === 'archived-courses' ? '' : ''}`}
              onClick={() => {
                handleSidebarItemClick('archived-courses', '/MHMV-profile/archived-courses');
              }}
            >
              <Archive size={20} />
              {sidebarExpanded && <span>Archived Courses</span>}
            </button>

            <div className="icv-sidebar-divider"></div>

            <button 
              className={`icv-sidebar-item ${activeSidebarItem === 'analytics' ? '' : ''}`}
              onClick={() => handleSidebarItemClick('analytics', '/MHMV-profile/instructor-analytics')}
            >
              <PieChart size={20} />
              {sidebarExpanded && <span>Analytics</span>}
            </button>
          </nav>

          {/* User Info */}
          <div className="icv-sidebar-user">
            <div className="icv-sidebar-user-avatar">
              {user?.email?.[0]?.toUpperCase() || 'U'}
            </div>
            {sidebarExpanded && (
              <div className="icv-sidebar-user-info">
                <span className="icv-sidebar-user-name">
                  {user?.displayName || user?.email?.split('@')[0] || 'User'}
                </span>
                <span className="icv-sidebar-user-email">{user?.email || 'user@example.com'}</span>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="icv-main">
        {/* Simple Top Bar */}
        <div className="icv-topbar">
          <div className="icv-topbar-left">
            <button 
              className="icv-home-btn"
              onClick={() => handleNavigation('/MHMV-profile/video-course-home')}
            >
              <Home size={20} />
              <span>Home</span>
            </button>
          </div>

          <div className="icv-topbar-right">
            <div className="icv-search-box">
              <Search size={18} className="icv-search-icon" />
              <input
                type="text"
                placeholder="Search your courses..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="icv-search-input"
              />
            </div>
            
            <button className="icv-notification-btn">
              <Bell size={20} />
              <span className="icv-notification-badge">3</span>
            </button>
            
            <UserDropdown 
              user={user}
              onLogout={handleLogout}
              onNavigate={handleNavigation}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="icv-content">
          {/* DELETE CONFIRMATION MODAL */}
          {showDeleteConfirm && (
            <div className="icv-modal-overlay">
              <div className="icv-confirm-modal">
                <h3>Delete Course</h3>
                <p>Are you sure you want to delete "{courseToDelete?.title}"? This action cannot be undone.</p>
                <div className="icv-modal-actions">
                  <button 
                    onClick={() => setShowDeleteConfirm(false)}
                    className="icv-cancel-btn"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={confirmDeleteCourse}
                    className="icv-delete-confirm-btn"
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Deleting...' : 'Delete Course'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* ARCHIVE CONFIRMATION MODAL */}
          {showArchiveConfirm && (
            <div className="icv-modal-overlay">
              <div className="icv-confirm-modal">
                <h3>Archive Course</h3>
                <p>Are you sure you want to archive "{courseToArchive?.title}"? You can unarchive it later from the archived courses page.</p>
                <div className="icv-modal-actions">
                  <button 
                    onClick={() => setShowArchiveConfirm(false)}
                    className="icv-cancel-btn"
                    disabled={actionLoading}
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={() => handleArchiveCourse(courseToArchive)}
                    className="icv-delete-confirm-btn"
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Archiving...' : 'Archive Course'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {detailView && selectedCourse ? (
            <CourseDetailView 
              course={selectedCourse}
              onBack={handleBackToList}
              user={user}
            />
          ) : (
            <div className="icv-courses-content">
              {/* Page Header */}
              <div className="icv-courses-header">
                <div className="icv-header-content">
                  <h1>My Courses</h1>
                  <p className="icv-courses-subtitle">
                    View and manage all your courses in one place
                  </p>
                </div>
                <div className="icv-header-actions">
                  <div className="icv-view-controls">
                    <button 
                      className={`icv-view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setViewMode('grid')}
                    >
                      <Grid size={16} />
                    </button>
                    <button 
                      className={`icv-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                      onClick={() => setViewMode('list')}
                    >
                      <List size={16} />
                    </button>
                  </div>
                  <button 
                    onClick={handleRefresh}
                    className="icv-refresh-btn"
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                  {user && (
                    <button 
                      onClick={() => handleNavigation('upload-excel')}
                      className="icv-create-btn"
                    >
                      <Upload size={14} />
                      Create Course
                    </button>
                  )}
                </div>
              </div>

              {/* Stats Summary */}
              <div className="icv-courses-stats">
                <div className="icv-stat-card">
                  <div className="icv-stat-card-value">{courses.length}</div>
                  <div className="icv-stat-card-label">Total Courses</div>
                </div>
                <div className="icv-stat-card">
                  <div className="icv-stat-card-value">
                    {courses.reduce((acc, course) => acc + (course.enrolledCount || 0), 0).toLocaleString()}
                  </div>
                  <div className="icv-stat-card-label">Total Students</div>
                </div>
                <div className="icv-stat-card">
                  <div className="icv-stat-card-value">
                    {courses.reduce((acc, course) => acc + getTotalVideosCount(course), 0)}
                  </div>
                  <div className="icv-stat-card-label">Total Videos</div>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="icv-error-message">
                  <p>{error}</p>
                </div>
              )}

              {/* Courses Grid */}
              {filteredCourses.length > 0 ? (
                <div className={`icv-courses-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                  {filteredCourses.map((course, index) => (
                    <InstructorCourseCard
                      key={course.courseId || index}
                      course={course}
                      onCourseSelect={handleCourseSelect}
                      onEditCourse={handleEditCourse}
                      onDeleteCourse={handleDeleteCourse}
                      onArchiveCourse={(course) => {
                        setCourseToArchive(course);
                        setShowArchiveConfirm(true);
                      }}
                      isArchived={false}
                    />
                  ))}
                </div>
              ) : (
                <div className="icv-empty-state">
                  <BookOpen size={40} />
                  <h3>No courses found</h3>
                  <p>
                    {searchQuery
                      ? 'No courses match your search. Try different keywords.'
                      : "You haven't created any courses yet. Start by creating your first course!"}
                  </p>
                  {user ? (
                    <button 
                      onClick={() => handleNavigation('upload-excel')}
                      className="icv-create-btn"
                      style={{ marginTop: '16px' }}
                    >
                      <Upload size={16} />
                      Create Your First Course
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleNavigation('login')}
                      className="icv-login-btn"
                    >
                      Sign in to create courses
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// Helper functions
const getTotalVideosCount = (course) => {
  let count = 0;
  course.modules?.forEach(module => {
    module.submodules?.forEach(submodule => {
      submodule.coursevideo?.forEach(courseVideo => {
        count += courseVideo.video?.length || 0;
      });
    });
  });
  return count;
};

const getTotalDuration = (course) => {
  const totalVideos = getTotalVideosCount(course);
  const totalMinutes = totalVideos * 30;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export default InstructorCoursesView;